<script>
  // ══════════════════════════════════════════════════════════════════════════
  // ЛИЧНОЕ ДЕЛО: Обзор · Статистика · Навыки — перенос sections/dossier.tsx 1:1.
  // В React это три экспорта одного файла; здесь один компонент, часть выбирает
  // проп part ('overview' | 'stats' | 'skills') — его даёт body-свитч mainmenu.
  // ══════════════════════════════════════════════════════════════════════════
  import { t } from '../../i18n.js';
  import { money, num, playtime, since, dmy } from '../partsfn.js';
  import { vehiclePhoto } from '../photofn.js';
  import { JOBS, VIPS, FRACTIONS, LICENSES } from '../demo.js';
  import Ico from '../ico.svelte';
  import Empty from '../empty.svelte';
  import Scroll from '../ui/scroll.svelte';
  import Sechead from '../ui/sechead.svelte';
  import Panel from '../ui/panel.svelte';
  import Metric from '../ui/metric.svelte';
  import Mgrid from '../ui/mgrid.svelte';
  import Prog from '../ui/prog.svelte';
  import Chip from '../ui/chip.svelte';
  import GoldBtn from '../ui/goldbtn.svelte';
  import GhostBtn from '../ui/ghostbtn.svelte';
  import Row from '../ui/row.svelte';
  import Rows from '../ui/rows.svelte';
  import Line from '../ui/line.svelte';
  import Grid from '../ui/grid.svelte';
  import PhotoCard from '../ui/photocard.svelte';
  import Jc from '../ui/jc.svelte';

  export let part = 'overview';
  export let char = {};
  export let vehs = [];
  export let quests = [];
  export let dailies = [];
  export let bpLevel = undefined;
  export let onGo = undefined;

  /** Прогресс до следующего уровня. Формула геймода: нужно (3 + LVL*3) респектов. */
  const levelPct = (c) => {
    const need2 = 3 + (c.LVL ?? 1) * 3;
    return Math.min(100, Math.round(((c.EXP ?? 0) / need2) * 100));
  };

  const go = (id) => { if (onGo) onGo(id); };

  // ── Обзор
  $: activeQuest = quests.find((q) => q.active) ?? quests[0];
  $: readyDaily = dailies.find((d) => d.status === 2);
  $: need = 3 + (char.LVL ?? 1) * 3;
  $: vip = VIPS[char.VipLvl ?? 0] ?? VIPS[0];
  // название фракции приходит с сервера (FractionName); демо-список остаётся
  // запасным вариантом, чтобы витрина без сервера всё ещё что-то показывала
  $: frac = char.FractionName || (char.FractionID ? FRACTIONS[char.FractionID] : null);

  // ── Статистика: три среза времени, вкладка живёт локально (useState → let)
  let tab = 'total';
  $: lic = char.Licenses ?? [];
  $: licHas = lic.filter(Boolean).length;
  $: time = tab === 'today' ? char.TodayTime : tab === 'month' ? char.MonthTime : char.TotalTime;

  // ── Навыки. Прогресс как в старом меню: ранг даёт 20%, внутри — current/nextLevel.
  $: skills = char.jobSkillsInfo ?? [];
  const pct = (s) =>
    s.currentLevel >= 5 ? 100 : Math.min(100, (s.currentLevel / 5) * 100 + (s.current / Math.max(1, s.nextLevel)) * 20);
</script>

{#if part === 'overview'}
  <Scroll>
    <Sechead kicker={t('m.ov.kicker')} title={char.Name || t('m.ov.title')}>
      <svelte:fragment slot="meta">{t('m.ov.level')} <b>{num(char.LVL ?? 1)}</b></svelte:fragment>
    </Sechead>

    <Mgrid>
      <Metric gold label={t('m.ov.cash')} value={money(char.Money)} />
      <Metric label={t('m.ov.bank')} value={money(char.BankMoney)} />
      <Metric label={t('m.ov.respect')} value={`${num(char.EXP)} / ${num(need)}`} smallValue />
      <Metric label={t('m.st.playtime')} value={playtime(char.TotalTime)} smallValue />
    </Mgrid>

    <!-- прогресс уровня — единственная широкая полоса раздела, задаёт ритм -->
    <Panel style="margin-top:12px">
      <div class="ui-field" style="margin-bottom:0">
        <div class="labrow">
          <span class="lab" style="margin-bottom:0">{t('m.ov.level')} {num(char.LVL ?? 1)}</span>
          <span class="note mn-num">{num(char.EXP)} / {num(need)}</span>
        </div>
        <Prog large value={levelPct(char)} />
      </div>
    </Panel>

    <div class="mn-grid wide" style="margin-top:var(--mn-gap);max-width:none">
      <Panel>
        <Sechead title={t('m.ov.status')} divider={false} />
        <Rows>
          <!-- Возраст в моде не хранится — строка с «24 года» была выдумкой макета, убрана.
               Пол приходит с сервера булевым (true = мужчина); прежнее сравнение с нулём
               давало «женщина» вообще всем. Работа — названием с сервера, а не из демо. -->
          <Line k={t('m.ov.gender')} v={char.Gender ? t('m.ov.male') : t('m.ov.female')} />
          <Line k={t('m.ov.job')} v={char.WorkName || JOBS[char.WorkID ?? 0] || JOBS[0]} />
          <Line k={t('m.ov.wedding')} v={char.WeddingName || t('m.ov.single')} tone={char.WeddingName ? undefined : 'off'} />
          {#if frac}<Line k={t('m.ov.fraction')} v={frac} tone="gold" />{/if}
          {#if frac}<Line k={t('m.ov.rank')} v={char.FractionRank || `${num(char.FractionLVL)} ${t('m.sk.rank')}`} />{/if}
          <Line k={t('m.ov.vip')} v={vip} tone={(char.VipLvl ?? 0) > 0 ? 'gold' : 'off'} />
          {#if (char.VipLvl ?? 0) > 0}<Line k={t('m.ov.vipUntil')} v={dmy(char.VipDate)} />{/if}
        </Rows>
      </Panel>

      <Panel>
        <Sechead title={t('m.ov.docs')} divider={false} />
        <Rows>
          <Line k={t('m.ov.passport')} v={`ID ${num(char.UUID)}`} tone="gold" />
          <Line
            k={t('m.ov.phone')}
            v={(char.Sim ?? -1) === -1 ? t('m.ov.noSim') : num(char.Sim)}
            tone={(char.Sim ?? -1) === -1 ? 'off' : undefined}
          />
          <Line k={t('m.ov.account')} v={`XXXX XXXX ${String(char.Bank ?? 0).slice(-4)}`} />
          <Line k={t('m.st.registered')} v={dmy(char.CreateDate)} />
          <Line k={t('m.st.warns')} tone={(char.Warns ?? 0) > 0 ? undefined : 'off'}>
            {num(char.Warns)} <span style="opacity:0.5;font-weight:700">/ 3</span>
          </Line>
        </Rows>
        <div style="margin-top:12px">
          <Chip tone={(char.Warns ?? 0) >= 2 ? 'neg' : 'pos'}>
            {(char.Warns ?? 0) >= 2 ? t('m.ov.watched') : t('m.ov.clean')}
          </Chip>
        </div>
      </Panel>

      <!-- третья колонка отвечает на вопрос «что делать дальше» — ради неё
          обзор и существует, иначе он дублировал бы «Статистику» -->
      <Panel>
        <Sechead title={t('m.ov.next')} divider={false} />
        <Rows>
          {#if activeQuest}
            <Line k={t('m.quests')} v={activeQuest.Title} />
          {:else}
            <Line k={t('m.quests')} v={t('m.q.none')} tone="off" />
          {/if}
          <Line
            k={t('m.dailies')}
            v={readyDaily ? t('m.d.day', { n: readyDaily.day }) : t('m.taken')}
            tone={readyDaily ? 'gold' : 'off'}
          />
          <Line k={t('m.battlepass')} v={t('m.bp.level', { n: bpLevel ?? 1 })} />
          <Line k={t('m.balance')} tone="gold"><Jc v={char.redbucks} /></Line>
        </Rows>
        <div class="ui-col mn-buy">
          {#if activeQuest}
            <GhostBtn block size="sm" on:click={() => go('quests')}>{t('m.q.title')}</GhostBtn>
          {/if}
          {#if readyDaily}
            <GoldBtn block size="sm" on:click={() => go('dailies')}>{t('m.take')}</GoldBtn>
          {/if}
        </div>
      </Panel>
    </div>

    {#if vehs.length > 0}
      <div style="margin-top:16px">
        <Sechead title={t('m.veh.title')} centerLine>
          <svelte:fragment slot="meta"><b>{vehs.length}</b></svelte:fragment>
        </Sechead>
      </div>
      <Grid size="wide">
        {#each vehs.slice(0, 4) as v, i (v.model + i)}
          <PhotoCard
            src={vehiclePhoto(v.model)} title={v.name || v.model}
            sub={v.number} shotCls="wide" on:click={() => go('vehicles')}
          />
        {/each}
      </Grid>
    {/if}
  </Scroll>

{:else if part === 'stats'}
  <Scroll>
    <Sechead kicker={t('m.st.kicker')} title={t('m.st.title')}>
      <svelte:fragment slot="meta">{t('m.st.registered')} <b>{dmy(char.CreateDate)}</b></svelte:fragment>
    </Sechead>

    <Mgrid>
      <Metric gold label={t('m.ov.level')} value={num(char.LVL ?? 1)} sub={`${num(char.EXP)} ${t('m.ov.respect').toLowerCase()}`} />
      <Metric label={t('m.st.registered')} value={since(char.CreateDate)} smallValue sub={dmy(char.CreateDate)} />
      <Metric label={t('m.st.warns')} value={`${num(char.Warns)} / 3`} sub={t('m.st.warnsActive')} />
      <Metric label={t('m.st.licenses')} value={`${licHas} / ${LICENSES.length}`} smallValue />
    </Mgrid>

    <Row wrap style="margin-top:var(--mn-gap);align-items:stretch">
      <Panel class="ui-grow" style="min-width:260px">
        <Sechead title={t('m.st.playtime')} divider={false} />
        <!-- три среза времени переключаются на месте — карточка не меняет высоту -->
        <div class="mn-slots" style="margin-bottom:12px">
          {#each [['today', 'm.st.today'], ['month', 'm.st.month'], ['total', 'm.st.total']] as pair (pair[0])}
            <div class={'mn-slot' + (tab === pair[0] ? ' on' : '')} on:click={() => (tab = pair[0])}>{t(pair[1])}</div>
          {/each}
        </div>
        <div style="font:800 30px var(--f-head);color:var(--gold);font-variant-numeric:tabular-nums">
          {playtime(time)}
        </div>
        <Rows>
          <Line k={t('m.st.today')} v={playtime(char.TodayTime)} />
          <Line k={t('m.st.month')} v={playtime(char.MonthTime)} />
          <Line k={t('m.st.total')} v={playtime(char.TotalTime)} tone="gold" />
        </Rows>
      </Panel>

      <Panel class="ui-grow" style="min-width:380px">
        <Sechead title={t('m.st.licenses')} meta={t('m.st.licOf', { n: licHas, all: LICENSES.length })} divider={false} />
        <div class="mn-lic">
          {#each LICENSES as name, i (name)}
            {@const ok = !!lic[i]}
            <div class={'l' + (ok ? ' ok' : '')}>
              <span class="mk"><Ico n={ok ? 'check' : 'minus'} s={12} /></span>
              <span class="ln">{name}</span>
            </div>
          {/each}
        </div>
      </Panel>
    </Row>
  </Scroll>

{:else}
  <Scroll>
    <Sechead kicker={t('m.sk.kicker')} title={t('m.sk.title')}>
      <svelte:fragment slot="meta">{t('m.sk.maxed')}: <b>{skills.filter((s) => s.currentLevel >= 5).length}</b> / {skills.length}</svelte:fragment>
    </Sechead>
    {#if skills.length === 0}
      <Panel><Empty icon="skills" text={t('m.sk.none')} /></Panel>
    {:else}
      <div class="mn-grid wide">
        {#each skills as s (s.name)}
          {@const maxed = s.currentLevel >= 5}
          <Panel flat>
            <div class="ui-field" style="margin-bottom:0">
              <div class="labrow">
                <span class="lab" style="margin-bottom:0">{s.name}</span>
                {#if maxed}
                  <Chip tone="gold">{t('m.sk.maxed')}</Chip>
                {:else}
                  <span class="note mn-num">{s.currentLevel} {t('m.sk.rank')}</span>
                {/if}
              </div>
              <Prog value={pct(s)} />
              <div class="hint mn-num" style="margin-top:7px">
                {t('m.sk.progress')}: {num(s.current)} / {num(s.nextLevel)}
              </div>
            </div>
          </Panel>
        {/each}
      </div>
    {/if}
  </Scroll>
{/if}
